import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Milk, Wheat } from 'lucide-react';
import { CTASection } from '../../shared/components/CTASection';
import { ProductCard } from '../../shared/components/ProductCard';
import { SectionHeading } from '../../shared/components/SectionHeading';
import { ValueCard } from '../../shared/components/ValueCard';
import { getProductBySlug } from '../../shared/data/products';
import { PageShell } from '../PageShell/index';

export function CattleFeed() {
  const product = getProductBySlug('cattle-feed')!;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'What are the benefits of ZFAST Cattle Feed?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'It is formulated with a balanced blend of bypass protein, essential minerals, vitamins, and digestible fiber to support rumen health, daily energy requirements, and milk production.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How should I transition my cow/buffalo to ZFAST feed?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Always transition gradually over 7 to 10 days by mixing ZFAST feed with your current feed in increasing proportions to avoid digestive upset.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Where can I purchase ZFAST feed?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Please contact our support team via our Contact Page or check our Dealer Partnership page for a distributor near you.'
        }
      }
    ]
  };

  return (
    <PageShell
      title="Cattle Feed"
      eyebrow="Animal Nutrition"
      path="/animal-nutrition/cattle-feed"
      description="Cattle feed is positioned for Indian dairy farms, supporting practical routines around energy, digestion, mineral balance, and animal condition."
      breadcrumbs={[{ label: 'Animal Nutrition', href: '/animal-nutrition' }, { label: 'Cattle Feed' }]}
      jsonLd={faqSchema}
    >
      {/* Product Highlight Section */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr]">
          <div className="overflow-hidden rounded-[1.4rem] bg-gradient-to-br from-forest to-leaf text-white shadow-soft">
            <img src={product.image} alt="Balanced cattle feed bags in factory storage" loading="eager" className="h-72 w-full object-cover" />
            <div className="p-8">
              <h2 className="text-4xl font-black">Balanced cattle nutrition for India’s growing farms.</h2>
              <p className="mt-5 leading-8 text-white/90">
                Every farm is different, but every herd needs consistent feeding routines. Zfast cattle feed supports practical nutrition planning around energy, digestion, mineral balance, animal condition, and dependable dealer supply.
              </p>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-black text-ink transition hover:-translate-y-0.5">
                Request Product Details <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="grid gap-4">
            {product.keyBenefits.map((benefit) => (
              <div key={benefit} className="flex gap-3 rounded-2xl bg-white p-5 shadow-sm border border-forest/5">
                <CheckCircle2 className="h-6 w-6 shrink-0 text-forest" />
                <p className="font-semibold text-charcoal/75">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Support Features */}
      <section className="bg-cream py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading title="What balanced cattle feed can support">
            <p>Designed to support digestion, daily energy, mineral balance, animal condition, and productive farm management when used as part of a complete feeding routine.</p>
          </SectionHeading>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <ValueCard icon={Wheat} title="Energy support" description="Helpful for daily feeding routines when matched with roughage and water access." />
            <ValueCard icon={Milk} title="Dairy management" description="Supports practical cow and buffalo feeding conversations for farmers and dealers." />
            <ValueCard icon={CheckCircle2} title="Product support" description="Guidance for pack size, product use, availability, and dealer supply enquiries." />
          </div>
        </div>
      </section>

      {/* Dosage & Recommended For Section */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-[1.4rem] border border-forest/10 bg-white p-8 shadow-sm">
            <h3 className="text-2xl font-black text-ink">Feeding &amp; Dosage Guidelines</h3>
            <p className="mt-4 text-charcoal/70">
              For optimal dairy yields and animal health, feed should be given according to the body weight, growth stage, and milk production.
            </p>
            <div className="mt-6 space-y-4">
              <div className="border-l-4 border-forest pl-4">
                <h4 className="font-black text-ink">Body Maintenance</h4>
                <p className="text-sm text-charcoal/70">1.5 - 2.0 kg of ZFAST Cattle Feed daily per animal.</p>
              </div>
              <div className="border-l-4 border-forest pl-4">
                <h4 className="font-black text-ink">Milk Production</h4>
                <p className="text-sm text-charcoal/70">Add 400g of feed for every 1 Liter of milk produced daily.</p>
              </div>
              <div className="border-l-4 border-forest pl-4">
                <h4 className="font-black text-ink">Dry &amp; Pregnant Animals</h4>
                <p className="text-sm text-charcoal/70">1.5 - 2.5 kg daily to support fetal development and body condition.</p>
              </div>
            </div>
            <p className="mt-6 text-xs text-charcoal/50">
              *Note: Ensure animals always have access to clean, fresh drinking water and adequate green and dry fodder.
            </p>
          </div>

          <div className="rounded-[1.4rem] border border-forest/10 bg-white p-8 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-black text-ink">Recommended For</h3>
              <p className="mt-4 text-charcoal/70">
                ZFAST feed is formulated to cover a wide variety of dairy cattle and farm requirements in India:
              </p>
              <ul className="mt-6 space-y-3">
                {product.suitableFor.map((item: string) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-forest" />
                    <span className="font-semibold text-charcoal/75">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-forest/10">
              <p className="text-sm text-charcoal/70">
                Interested in testing or stocking ZFAST feed? Learn more about our{' '}
                <Link to="/quality-research" className="font-bold text-forest hover:underline">
                  read our quality approach
                </Link>{' '}
                or apply for a{' '}
                <Link to="/dealer-partnership" className="font-bold text-forest hover:underline">
                  become a ZFAST dealer
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-cream py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-ink">Frequently Asked Questions</h3>
            <p className="mt-4 text-charcoal/70">Find quick answers about ZFAST Cattle Feed nutrition and usage.</p>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">What are the benefits of ZFAST Cattle Feed?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                It is formulated with a balanced blend of bypass protein, essential minerals, vitamins, and digestible fiber to support rumen health, daily energy requirements, and milk production.
              </p>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">How should I transition my cow/buffalo to ZFAST feed?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                Always transition gradually over 7 to 10 days by mixing ZFAST feed with your current feed in increasing proportions (e.g., 25% on days 1-3, 50% on days 4-6, 75% on days 7-9, and 100% on day 10) to avoid digestive upset.
              </p>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">Where can I purchase ZFAST feed?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                Please contact our support team via <Link to="/contact" className="text-forest font-bold hover:underline">Contact Page</Link> or check our <Link to="/dealer-partnership" className="text-forest font-bold hover:underline">Dealer Partnership</Link> page for a distributor near you.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Need product guidance?" description="Share your animal type, product requirement, and location so our team can help." primaryLabel="Contact Us" primaryHref="/contact" />
    </PageShell>
  );
}
